import {WithTranslation, withTranslation} from 'react-i18next'
import classNames from 'classnames'

import {ReactComponent as AvailableTokens} from '../../../../assets/icons/available-tokens.svg'
import {ReactComponent as Calendar} from '../../../../assets/icons/calendar-small.svg'
import {ReactComponent as CalendarTime} from '../../../../assets/icons/calendar-time.svg'
import {ReactComponent as Coins} from '../../../../assets/icons/coins.svg'
import {ReactComponent as ColumnChart} from '../../../../assets/icons/column-chart.svg'
import {ReactComponent as EstimatedReturnIcon} from '../../../../assets/icons/estimated-return.svg'
import {ReactComponent as PieChart} from '../../../../assets/icons/pie-chart-white.svg'
import {ReactComponent as Redirect} from '../../../../assets/icons/redirect.svg'
import {ReactComponent as Star} from '../../../../assets/icons/star.svg'
import {ReactComponent as StopWatch} from '../../../../assets/icons/stopwatch.svg'
import Card from '../../../../components/Card/Card'
import ItemProperty from '../../../../components/ItemCard/components/ItemProperty/ItemProperty'
import {useProjectSlice} from '../../../../store/slices/project'
import {formatDate} from '../../../../utils/formatDate'
import {formatNumber} from '../../../../utils/formatNumber'

import styles from './TokenomicInfo.module.scss'

interface ITokenomicInfoProps extends WithTranslation {
  withShadow?: boolean
  straightCorners?: boolean
}

const TokenomicInfo = ({t, straightCorners}: ITokenomicInfoProps) => {
  const {
    tokenomic: {
      minimum_investment,
      project_duration,
      dividend_period,
      rent_start_date,
      return_over_investment,
      estimated_return,
      rental_return_over_investment,
      token,
    },
  } = useProjectSlice()

  return (
    <Card className={styles.card} withShadow straightCorners={straightCorners}>
      <div
        className={classNames(styles.investmentBox, {[styles.straightCorners]: straightCorners})}>
        {!!project_duration && (
          <ItemProperty
            icon={<Calendar className={styles['path-fill']} />}
            label={`${t('item.projectDuration')}`}
            property={
              <span>
                {project_duration}{' '}
                {(+project_duration > 1 ? t('months') : t('month'))?.toLowerCase()}
              </span>
            }
            oneLine
          />
        )}

        {!!minimum_investment && (
          <ItemProperty
            icon={<ColumnChart className={styles['path-fill']} />}
            label={`${t('item.minimumInvestment')}`}
            property={
              <span className={styles.percentage}>{`${+minimum_investment || 0} token${
                (+minimum_investment > 1 && 's') || ''
              }`}</span>
            }
            oneLine
          />
        )}
        <div className={styles.line} />
        {!!dividend_period && (
          <ItemProperty
            icon={<CalendarTime className={styles['path-fill']} />}
            label={`${t('item.estimatedPeriod')}`}
            property={
              <span>
                {dividend_period} {(+dividend_period > 1 ? t('months') : t('month'))?.toLowerCase()}
              </span>
            }
            oneLine
          />
        )}
        {!!token?.available_stock && (
          <ItemProperty
            icon={<AvailableTokens className={styles['path-stroke']} />}
            label={`${t('item.availableTokens')}`}
            property={<span>{formatNumber(+token.available_stock || 0)}</span>}
            oneLine
          />
        )}
        {!!rent_start_date && (
          <ItemProperty
            icon={<StopWatch className={styles['path-fill']} />}
            label={`${t('item.rentStartDate')}`}
            property={<span className={styles.label}>{formatDate(rent_start_date)}</span>}
            oneLine
          />
        )}
        {!!return_over_investment && (
          <ItemProperty
            icon={<EstimatedReturnIcon className={styles['path-stroke-fill']} />}
            label={`${t('item.estimatedAnnualReturn')}`}
            property={
              <span className={styles.percentage}>
                {formatNumber(+return_over_investment || 0)}%
              </span>
            }
            oneLine
          />
        )}
        {!!estimated_return && (
          <ItemProperty
            icon={<PieChart className={styles['path-fill']} />}
            label={`${t('item.projectRateOfReturn')}`}
            property={
              <span className={styles.percentage}>{formatNumber(+estimated_return || 0)}%</span>
            }
            oneLine
          />
        )}
        {!!rental_return_over_investment && (
          <ItemProperty
            icon={<Coins className={styles['path-stroke']} />}
            label={`${t('item.rentalReturnOverInvestment')}`}
            property={
              <span className={styles.percentage}>
                {formatNumber(+rental_return_over_investment || 0)}%
              </span>
            }
            oneLine
          />
        )}

        {!!token?.name && !!token?.nft_contract_address && (
          <ItemProperty
            icon={<Star className={styles['path-fill']} />}
            label={`${t('item.tokenDetail')}`}
            property={
              <span className={styles.redirect}>
                {token?.name}
                <a
                  className={styles.buttonRedirect}
                  href={`https://mumbai.polygonscan.com/token/${token?.nft_contract_address}`}
                  target="_blank"
                  rel="noopener noreferrer">
                  <Redirect />
                </a>
              </span>
            }
            oneLine
          />
        )}
      </div>
    </Card>
  )
}

export default withTranslation()(TokenomicInfo)
